import { useEffect, useRef } from 'react';

/**
 * DynamicBackground — renders the organization-configured onboarding background
 * (gradient, image, or video) on the landing/login pages. The shape matches what
 * customer admins save via OrgBrandingConfig in the admin UI (`background_config`
 * column on the `organizations` table).
 *
 * Mirrors the component used by `apps/pwa-handel` so that the same design
 * configured under "Mobilapplikation" appears on every org-facing entry point.
 */
export interface BackgroundConfig {
  type: 'video' | 'image' | 'gradient';
  video_url?: string;
  video_webm_url?: string;
  image_url?: string;
  gradient_from?: string;
  gradient_via?: string;
  gradient_to?: string;
  /** 0-1 or 0-100. Values > 1 are treated as percent. */
  opacity?: number;
}

interface DynamicBackgroundProps {
  config?: BackgroundConfig | null;
  className?: string;
}

const SAFE_AREA_STYLE: React.CSSProperties = {
  top: 'calc(env(safe-area-inset-top, 0px) * -1)',
  left: 'calc(env(safe-area-inset-left, 0px) * -1)',
  right: 'calc(env(safe-area-inset-right, 0px) * -1)',
  bottom: 'calc(env(safe-area-inset-bottom, 0px) * -1)',
};

function normalizeOpacity(raw?: number): number {
  if (raw === undefined || raw === null) return 0.85;
  // Admin stores 0-1 (slider divides by 100). Accept 0-100 as well.
  return raw > 1 ? raw / 100 : raw;
}

export function DynamicBackground({ config, className = '' }: DynamicBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (config?.type !== 'video' || !videoRef.current) return;
    const video = videoRef.current;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;

    const tryPlay = () => {
      video.play().catch(() => {
        // iOS requires user gesture sometimes; retry shortly
        setTimeout(tryPlay, 500);
      });
    };

    const timeout = setTimeout(() => {
      video.load();
      tryPlay();
    }, 100);

    return () => clearTimeout(timeout);
  }, [config?.type, config?.video_url, config?.video_webm_url]);

  if (!config) return null;

  const opacity = normalizeOpacity(config.opacity);

  if (config.type === 'video' && (config.video_url || config.video_webm_url)) {
    return (
      <div
        aria-hidden="true"
        className={`fixed inset-0 z-0 pointer-events-none ${className}`}
        style={SAFE_AREA_STYLE}
      >
        <video
          key={`${config.video_url ?? ''}-${config.video_webm_url ?? ''}`}
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          muted
          loop
          playsInline
          preload="auto"
          style={{ opacity }}
        >
          {config.video_webm_url && <source src={config.video_webm_url} type="video/webm" />}
          {config.video_url && <source src={config.video_url} type="video/mp4" />}
        </video>
      </div>
    );
  }

  if (config.type === 'image' && config.image_url) {
    return (
      <div
        aria-hidden="true"
        className={`fixed inset-0 z-0 pointer-events-none ${className}`}
        style={SAFE_AREA_STYLE}
      >
        <img
          src={config.image_url}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          style={{ opacity }}
        />
      </div>
    );
  }

  if (config.type === 'gradient' && config.gradient_from) {
    const gradient = config.gradient_via
      ? `linear-gradient(135deg, ${config.gradient_from} 0%, ${config.gradient_via} 50%, ${config.gradient_to ?? config.gradient_from} 100%)`
      : `linear-gradient(135deg, ${config.gradient_from} 0%, ${config.gradient_to ?? config.gradient_from} 100%)`;

    return (
      <div
        aria-hidden="true"
        className={`fixed inset-0 z-0 pointer-events-none ${className}`}
        style={{ ...SAFE_AREA_STYLE, background: gradient, opacity }}
      />
    );
  }

  return null;
}
